import { actionSmartLoadContact } from "@/actions/smartLoadContact.action"
import { messengerStore$ } from "@/app/(normal)/messenger/messenger.store"
import { chatStore$ } from "@/lib/ContactsMap"
import { TInteraction } from "@/schemas/contacts"
import { dynamoStore$ } from "@/stores/dynamo.v2.store"
import { syncStore$ } from "@/stores/sync.v2.store"

/**
 * Selects a contact and loads its details, optionally based on the last interaction.
 *
 * IMPORTANT: Make sure that if this function is called inside a component, the component is wrapped inside an `observer` or `reactiveObserver` function.
 *
 * @param {Object} params - The parameters for selecting the contact.
 * @param {string} params.contactPersonID - The ID of the contact person to select.
 * @param {TInteraction} [params.lastInteraction] - The last interaction with the contact, if available.
 *
 * @throws Will revert the smart view contact ID if the server action fails.
 *
 * @remarks
 * This function updates the smart view contact ID before making a server call to load the contact details.
 * If the last interaction's RingId is not a valid number, it searches for the latest interaction with a valid numeric RingId.
 * If the server action fails, it reverts the smart view contact ID to its previous value.
 */
export async function selectContact({ contactPersonID, lastInteraction }: { contactPersonID: string; lastInteraction?: TInteraction | null }) {
  const currentContactId = messengerStore$.selectedContactID.peek()

  // @deprecated The embedded frontend webphone is disabled. The Chrome extension now owns focusing the webphone sidebar.
  // if (typeof window !== "undefined" && (window as unknown as { focusDetachedWebphone?: () => void }).focusDetachedWebphone) {
  //   ;(window as unknown as { focusDetachedWebphone: () => void }).focusDetachedWebphone()
  // }

  try {
    let lastInteractionRingId: number | undefined = undefined

    if (lastInteraction?.RingId) {
      const parsed = Number.parseInt(lastInteraction.RingId, 10)

      if (Number.isNaN(parsed)) {
        // Look for the latest interaction with a valid numeric ringID
        // This is not really performant, maybe in the future
        // we should improve this
        const interactions = chatStore$.sortedInteractions[contactPersonID].peek() ?? []

        for (let i = interactions.length - 1; i >= 0; i--) {
          const interaction = chatStore$.interactions[contactPersonID][interactions[i]].peek()
          if (!interaction.RingId) continue

          const parsed = Number.parseInt(interaction.RingId, 10)
          if (!Number.isNaN(parsed)) {
            lastInteractionRingId = parsed
            break
          }
        }
      } else {
        lastInteractionRingId = parsed
      }
    }

    // We need this after the promise since if we switch the contact in the webphone
    // using the ring id, the smart view sync update does not contain
    // the contact id, so we need to update it here
    // We run it before the server call so it feels more fluid
    const numericContactPersonID = Number.parseInt(contactPersonID, 10)
    messengerStore$.selectedContactID.set(numericContactPersonID.toString())

    await actionSmartLoadContact({
      contactPersonId: numericContactPersonID,
      ringId: lastInteractionRingId,
    })
  } catch {
    // If the server action fails, we need to revert the smart view contact id
    messengerStore$.selectedContactID.set(currentContactId)
  }
}

/**
 * Retrieves the last interaction for a given contact person.
 *
 * IMPORTANT: Make sure that if this function is called inside a component, the component is wrapped inside an `observer` or `reactiveObserver` function.
 *
 * @param {Object} params - The parameters for the function.
 * @param {string} params.contactPersonID - The ID of the contact person.
 * @param {string} [params.userPersonID] - The optional ID of the user person.
 * @returns {TInteraction | null} - The last interaction object or null if no interactions are found.
 *
 * @example
 * // Returns the last interaction for the contact person with ID '12345'
 * const lastInteraction = getLastInteraction({ contactPersonID: '12345' });
 *
 * @example
 * // Returns the last interaction for the contact person with ID '12345' and user person with ID '67890'
 * const lastInteraction = getLastInteraction({ contactPersonID: '12345', userPersonID: '67890' });
 */
export function getLastInteraction({ contactPersonID, userPersonID }: { contactPersonID: string; userPersonID?: string }): TInteraction | null {
  const interactions = dynamoStore$.recentContactInteractions[contactPersonID].peek() ? dynamoStore$.recentContactInteractions[contactPersonID].sorted.get() : []

  if (interactions.length === 0) {
    return null // Return null if there are no interactions
  }

  // TODO: This code should be cleaned and improved later on, there is no point of
  // having these weird anonymous functions

  if (!userPersonID) {
    const lastInteraction = (() => {
      for (let i = interactions.length - 1; i >= 0; i--) {
        // I also skip live calls since they are not considered interactions (they are not stored in the database but in the frontend)
        if (interactions[i].Type !== "liveCall") {
          return interactions[i]
        }
      }
      return null
    })()
    return lastInteraction
  }

  const lastInteraction = (() => {
    for (let i = interactions.length - 1; i >= 0; i--) {
      // I also skip live calls since they are not considered interactions (they are not stored in the database but in the frontend)
      if (interactions[i].UserPersonId === userPersonID && interactions[i].Type !== "liveCall") {
        return interactions[i]
      }
    }
    return null
  })()

  return lastInteraction
}
